import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Save, AlertTriangle, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

export default function BlogEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // SEO-State
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [focusKeyword, setFocusKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [slug, setSlug] = useState('');
  const [autoGenerateSEO, setAutoGenerateSEO] = useState(true);
  const [seoAlreadyGenerated, setSeoAlreadyGenerated] = useState(false);
  
  // Raw HTML Mode State
  const [contentMode, setContentMode] = useState<'editor' | 'raw'>('editor');
  const [rawHtml, setRawHtml] = useState('');
  
  // Title & Featured Image State
  const [title, setTitle] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isGeneratingSEO, setIsGeneratingSEO] = useState(false);
  const [isRestructuringHtml, setIsRestructuringHtml] = useState(false);
  const [previousHtml, setPreviousHtml] = useState('');

  // Post laden
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', id],
    queryFn: async () => {
      if (!id || id === ':id') {
        throw new Error('Ungültige Post-ID');
      }
      
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) {
        throw new Error('Blog-Post nicht gefunden');
      }
      return data;
    },
    enabled: !!id && id !== ':id'
  });

  // SEO-Felder und Content-Mode initialisieren wenn Post geladen
  useEffect(() => {
    if (post) {
      setMetaTitle(post.meta_title || '');
      setMetaDescription(post.meta_description || '');
      setFocusKeyword(post.focus_keyword || '');
      setCategory(post.category || '');
      setSlug(post.slug || '');
      setContentMode((post.content_mode as 'editor' | 'raw') || 'editor');
      setTitle(post.title || '');
      setFeaturedImage(post.featured_image || '');
      if (post.content_mode === 'raw') {
        setRawHtml(post.raw_html || '');
      }
      // Reset SEO generation flag when loading new post
      setSeoAlreadyGenerated(false);
    }
  }, [post?.id]);

  // Tiptap Editor initialisieren
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
    ],
    content: post?.content || '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[500px] p-6 border rounded-lg bg-card',
      },
    },
  });

  // Content in Editor laden wenn Post geladen wurde
  useEffect(() => {
    if (post?.content && editor) {
      editor.commands.setContent(post.content);
    }
  }, [post?.content, editor]);

  // Speichern-Mutation
  const saveMutation = useMutation({
    mutationFn: async ({ 
      generatedMetaTitle, 
      generatedMetaDescription,
      generatedFocusKeyword,
      generatedCategory
    }: { 
      generatedMetaTitle?: string; 
      generatedMetaDescription?: string;
      generatedFocusKeyword?: string;
      generatedCategory?: string;
    } = {}) => {
      // CRITICAL: Prevent saving with invalid ID
      if (!id || id === ':id') {
        throw new Error('Ungültige Post-ID. Bitte wähle einen Post aus dem Dashboard.');
      }

      const updateData = contentMode === 'raw' 
        ? { 
            content_mode: 'raw',
            raw_html: rawHtml,
            content: null,
          }
        : {
            content_mode: 'editor',
            content: editor?.getHTML() || '',
            raw_html: null,
          };
      
      // Use generated values if provided, otherwise use state
      const finalMetaTitle = generatedMetaTitle ?? metaTitle;
      const finalMetaDescription = generatedMetaDescription ?? metaDescription;
      const finalFocusKeyword = generatedFocusKeyword ?? focusKeyword;
      const finalCategory = generatedCategory ?? category;

      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          ...updateData,
          slug: slug,
          meta_title: finalMetaTitle,
          meta_description: finalMetaDescription,
          focus_keyword: finalFocusKeyword,
          category: finalCategory,
          title: title,
          featured_image: featuredImage,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ 
        title: '✅ Gespeichert!',
        description: 'Deine Änderungen wurden erfolgreich gespeichert.'
      });
      queryClient.invalidateQueries({ queryKey: ['blog-post', id] });
    },
    onError: (error: Error) => {
      toast({
        title: '❌ Fehler beim Speichern',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  const handleSave = async () => {
    let generatedTitle: string | undefined;
    let generatedDescription: string | undefined;
    let generatedKeyword: string | undefined;
    let generatedCat: string | undefined;

    // AI-SEO-Generierung VOR dem Speichern (nur bei Raw HTML Mode + Toggle aktiviert + noch nicht generiert)
    if (contentMode === 'raw' && autoGenerateSEO && !seoAlreadyGenerated && rawHtml.trim()) {
      setIsGeneratingSEO(true);
      try {
        const { data, error } = await supabase.functions.invoke('generate-seo-metadata', {
          body: { htmlContent: rawHtml }
        });

        if (error) throw error;

        if (data?.metaTitle && data?.metaDescription && data?.focusKeyword && data?.category) {
          generatedTitle = data.metaTitle;
          generatedDescription = data.metaDescription;
          generatedKeyword = data.focusKeyword;
          generatedCat = data.category;
          
          // Update UI state for immediate display
          setMetaTitle(data.metaTitle);
          setMetaDescription(data.metaDescription);
          setFocusKeyword(data.focusKeyword);
          setCategory(data.category);
          setSeoAlreadyGenerated(true); // Flag setzen: SEO wurde generiert
          
          toast({
            title: '✨ SEO-Metadaten generiert',
            description: 'Alle SEO-Felder wurden automatisch ausgefüllt.'
          });
        }
      } catch (error) {
        console.error('SEO generation error:', error);
        toast({
          title: '⚠️ SEO-Generierung fehlgeschlagen',
          description: 'Post wird ohne automatische SEO-Daten gespeichert.',
          variant: 'destructive'
        });
      } finally {
        setIsGeneratingSEO(false);
      }
    }
    
    // Pass generated values directly to mutation to avoid race condition
    saveMutation.mutate({ 
      generatedMetaTitle: generatedTitle,
      generatedMetaDescription: generatedDescription,
      generatedFocusKeyword: generatedKeyword,
      generatedCategory: generatedCat
    });
  };

  // Image Upload Handler
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: '❌ Ungültiger Dateityp',
        description: 'Bitte lade nur Bilddateien hoch (JPG, PNG, WEBP)',
        variant: 'destructive'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: '❌ Datei zu groß',
        description: 'Das Bild darf maximal 5MB groß sein',
        variant: 'destructive'
      });
      return;
    }

    setIsUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `blog-covers/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      setFeaturedImage(publicUrl);

      toast({
        title: '✅ Bild hochgeladen!',
        description: 'Das Cover-Bild wurde erfolgreich gespeichert.'
      });
    } catch (error: any) {
      toast({
        title: '❌ Upload fehlgeschlagen',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFeaturedImage('');
    toast({
      title: '🗑️ Bild entfernt',
      description: 'Das Cover-Bild wurde entfernt. Vergiss nicht zu speichern!'
    });
  };

  // HTML neu strukturieren
  const handleRestructureHtml = async () => {
    if (!rawHtml.trim()) {
      toast({
        title: '❌ Kein Content',
        description: 'Bitte füge zuerst HTML-Content ein.',
        variant: 'destructive'
      });
      return;
    }

    setPreviousHtml(rawHtml);
    setIsRestructuringHtml(true);

    const wordCount = rawHtml.split(/\s+/).length;
    const estimatedMinutes = Math.ceil(wordCount / 1000);

    try {
      toast({
        title: 'HTML wird strukturiert...',
        description: `Geschätzter Zeitaufwand: ~${estimatedMinutes} Min. Der Content wird automatisch in Abschnitte aufgeteilt falls nötig.`,
        duration: estimatedMinutes * 60 * 1000
      });

      const { data, error } = await supabase.functions.invoke('restructure-blog-html', {
        body: { 
          rawHtml,
          title,
          category,
          wordCount
        }
      });

      if (error) {
        console.error('Edge Function Error:', error);
        throw new Error(error.message || 'HTML-Strukturierung fehlgeschlagen');
      }

      if (!data?.structuredHtml) {
        throw new Error('Keine strukturierten HTML-Daten erhalten');
      }

      setRawHtml(data.structuredHtml);
      
      // CRITICAL: Save restructured HTML to database
      const { error: updateError } = await supabase
        .from('blog_posts')
        .update({ raw_html: data.structuredHtml })
        .eq('id', id);

      if (updateError) {
        console.error('Database update failed:', updateError);
        toast({
          title: '⚠️ Speichern fehlgeschlagen',
          description: 'HTML wurde im Editor aktualisiert, aber nicht in der Datenbank gespeichert.',
          variant: 'destructive'
        });
        return;
      }
      
      const chunksInfo = data.chunksProcessed > 1 ? ` in ${data.chunksProcessed} Abschnitten` : '';
      toast({
        title: '✨ HTML strukturiert & gespeichert!',
        description: `${data.structuredHtml.length} Zeichen${chunksInfo} verarbeitet`,
        action: (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              setRawHtml(previousHtml);
              toast({ title: '↩️ Wiederhergestellt' });
            }}
          >
            Rückgängig
          </Button>
        )
      });

    } catch (error: any) {
      console.error('HTML-Strukturierung fehlgeschlagen:', error);
      toast({
        title: '❌ Strukturierung fehlgeschlagen',
        description: error.message || 'Unbekannter Fehler',
        variant: 'destructive'
      });
    } finally {
      setIsRestructuringHtml(false);
    }
  };

  // Slug-Generierung aus Text
  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[äöüß]/g, char => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[char as keyof { ä: string; ö: string; ü: string; ß: string }] || char))
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Zeichenzähler-Farbe basierend auf Länge (Soft-Limits)
  const getTitleColor = (length: number) => {
    if (length >= 50 && length <= 60) return 'text-green-500';
    return 'text-muted-foreground';
  };

  const getDescriptionColor = (length: number) => {
    if (length >= 150 && length <= 160) return 'text-green-500';
    return 'text-muted-foreground';
  };

  if (isLoading) {
    return (
      <>
        <Navigation />
        <div className="container mx-auto p-8">
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Lädt Editor...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Guard: Prevent access without valid post ID
  if (!id || id === ':id') {
    return (
      <>
        <Navigation />
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
        </div>
        <div className="container mx-auto p-8 relative z-10">
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center">
                <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
                <h2 className="text-2xl font-bold mb-2">Keine Post-ID angegeben</h2>
                <p className="text-muted-foreground mb-6">
                  Bitte wähle einen Blog-Post aus dem Dashboard zum Bearbeiten.
                </p>
                <Button onClick={() => navigate('/admin/blog')} variant="outline">
                  ← Zurück zum Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-md w-full border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Fehler beim Laden
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {error.message === 'Blog-Post nicht gefunden' 
                  ? 'Dieser Blog-Post existiert nicht oder wurde gelöscht.'
                  : error.message
                }
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={() => navigate('/admin/blog')}
                  className="flex-1"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Zurück zur Übersicht
                </Button>
                <Button 
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['blog-post', id] })}
                  variant="outline"
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Erneut versuchen
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      
      {/* Wellenförmiger Hintergrund */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_20%,hsl(var(--accent)/0.08)_0%,hsl(var(--accent)/0.04)_30%,transparent_60%)] opacity-60"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_100%_at_50%_60%,hsl(var(--accent)/0.10)_0%,hsl(var(--accent)/0.05)_35%,transparent_65%)] opacity-70"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_80%_at_50%_90%,hsl(var(--accent)/0.06)_0%,hsl(var(--accent)/0.02)_40%,hsl(var(--background))_75%)]"></div>
      </div>

      <div className="container mx-auto p-8 relative z-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate('/admin/blog')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{title || post?.title || 'Neuer Blog-Post'}</h1>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Published Toggle */}
          <div className="flex items-center gap-2 bg-muted px-3 py-2 rounded-lg">
            <Switch 
              id="published-toggle"
              checked={post?.published || false}
              onCheckedChange={async (checked) => {
                await supabase
                  .from('blog_posts')
                  .update({ published: checked })
                  .eq('id', id);
                
                queryClient.invalidateQueries({ queryKey: ['blog-post', id] });
                toast({ 
                  title: checked ? '✅ Veröffentlicht!' : '📝 Als Entwurf gespeichert',
                  description: checked ? 'Post ist jetzt öffentlich sichtbar' : 'Post ist nur im Admin sichtbar'
                });
              }}
            />
            <Label htmlFor="published-toggle" className="cursor-pointer text-sm font-medium">
              {post?.published ? '✅ Live' : '📝 Entwurf'}
            </Label>
          </div>

          {/* Preview Button */}
          <Button 
            variant="outline"
            onClick={() => window.open(`/blog/${post?.slug}`, '_blank')}
            disabled={!post?.slug}
            className="gap-2"
          >
            Vorschau
          </Button>

          {/* Save Button */}
          <Button 
            onClick={handleSave}
            disabled={saveMutation.isPending || isGeneratingSEO || !id || id === ':id'}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            {isGeneratingSEO ? 'Generiere SEO...' : saveMutation.isPending ? 'Speichert...' : 'Speichern'}
          </Button>
        </div>
      </div>

      {/* 2-Spalten-Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-6">
        {/* Linke Spalte: Editor */}
        <Card>
          <CardHeader>
            <CardTitle>Content</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Title & Cover Image Section */}
            <div className="space-y-6 mb-8 pb-8 border-b">
              {/* Blog Title Input */}
              <div className="space-y-2">
                <Label htmlFor="blog-title" className="text-base font-semibold">
                  📝 Blog-Titel
                </Label>
                <Input
                  id="blog-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Gib hier den Titel deines Blog-Posts ein..."
                  className="text-xl font-semibold h-12"
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground">
                  {title.length}/100 Zeichen
                  {title.length > 60 && ' ⚠️ Titel könnte zu lang für die Übersicht sein'}
                  <span className="block mt-1">💡 HTML-Tags wie <code className="px-1 py-0.5 bg-muted rounded text-xs">&lt;br&gt;</code> sind erlaubt</span>
                </p>
              </div>

              {/* Cover Image Upload */}
              <div className="space-y-3">
                <Label htmlFor="cover-image" className="text-base font-semibold">
                  🖼️ Cover-Bild / Featured Image
                </Label>
                
                {featuredImage ? (
                  <div className="relative group">
                    <img 
                      src={featuredImage} 
                      alt="Cover Preview" 
                      className="w-full h-48 object-cover rounded-lg border"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleRemoveImage}
                      >
                        🗑️ Entfernen
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => document.getElementById('cover-image')?.click()}
                      >
                        🔄 Ersetzen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label 
                    htmlFor="cover-image"
                    className="flex flex-col items-center justify-center h-48 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                  >
                    <div className="text-center p-6">
                      <svg 
                        className="mx-auto h-12 w-12 text-muted-foreground mb-3" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
                        />
                      </svg>
                      <p className="text-sm font-medium mb-1">
                        {isUploadingImage ? 'Lädt hoch...' : 'Klicke zum Hochladen'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        JPG, PNG oder WEBP (max. 5MB)
                      </p>
                    </div>
                  </label>
                )}
                
                <input
                  id="cover-image"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageUpload}
                  disabled={isUploadingImage}
                  className="hidden"
                />
                
                <p className="text-xs text-muted-foreground">
                  💡 Tipp: Empfohlene Größe 1200x630px (ideal für Social Media Sharing)
                </p>
              </div>
            </div>

            {/* Content Mode Toggle */}
            <div className="flex items-center justify-between mb-6 p-4 border rounded-lg bg-muted/30">
              <div>
                <h3 className="font-semibold">Content-Modus</h3>
                <p className="text-sm text-muted-foreground">
                  {contentMode === 'raw' 
                    ? '⚠️ Raw HTML: Vollständige Kontrolle mit Styles'
                    : '✅ Editor: Sicherer WYSIWYG-Editor'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {contentMode === 'editor' ? '📝 Editor' : '💻 Raw HTML'}
                </span>
                <Switch 
                  checked={contentMode === 'raw'}
                  onCheckedChange={(checked) => setContentMode(checked ? 'raw' : 'editor')}
                />
              </div>
            </div>

            {contentMode === 'raw' ? (
              // Raw HTML Mode
              <div className="space-y-4">
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>⚠️ Achtung: Raw HTML-Modus</AlertTitle>
                  <AlertDescription>
                    HTML wird UNVERÄNDERT gespeichert inkl. &lt;style&gt;-Tags. 
                    Perfekt für KI-generierten Content. Stelle sicher, dass der Code vertrauenswürdig ist.
                  </AlertDescription>
                </Alert>

                {/* HTML Restructure Button */}
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                  <div className="flex-1">
                    <h4 className="font-semibold text-sm">🎨 Design-System anwenden</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      Wandle einfachen HTML-Text in strukturiertes Layout mit Icons, Cards & Accordions um
                    </p>
                  </div>
                  <Button
                    onClick={handleRestructureHtml}
                    disabled={isRestructuringHtml || !rawHtml.trim()}
                    variant="outline"
                    className="ml-4"
                  >
                    {isRestructuringHtml ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Strukturiert...
                      </>
                    ) : (
                      <>
                        🎨 HTML neu strukturieren
                      </>
                    )}
                  </Button>
                </div>
                
                <Textarea
                  value={rawHtml}
                  onChange={(e) => setRawHtml(e.target.value)}
                  className="font-mono text-sm min-h-[600px]"
                  placeholder={`<!DOCTYPE html>
<html lang="de">
<head>
  <style>
    /* Deine Custom Styles hier */
    body { font-family: Inter, sans-serif; }
    h1 { color: #00e0ff; }
  </style>
</head>
<body>
  <h1>Dein Content</h1>
  <p>HTML mit vollständigem Styling...</p>
</body>
</html>`}
                />
                
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Raw HTML wird isoliert gerendert (Shadow DOM) - Styles beeinflussen nicht die Seite
                </div>
              </div>
            ) : (
              // Editor Mode (existing code)
              <>
            {/* Tabs: Visual / HTML */}
            <Tabs defaultValue="visual" className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md mb-4">
                <TabsTrigger value="visual">✨ Visual Editor</TabsTrigger>
                <TabsTrigger value="html">💻 HTML Code</TabsTrigger>
              </TabsList>

              {/* Tab 1: Visual Editor (Tiptap) */}
              <TabsContent value="visual" className="mt-0">
                {/* Toolbar */}
                {editor && (
                  <div className="flex gap-2 mb-4 p-2 border rounded-lg bg-muted/50 flex-wrap">
                    <Button
                      variant={editor.isActive('bold') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleBold().run()}
                    >
                      <strong>B</strong>
                    </Button>
                    <Button
                      variant={editor.isActive('italic') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleItalic().run()}
                    >
                      <em>I</em>
                    </Button>
                    <Button
                      variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                    >
                      H1
                    </Button>
                    <Button
                      variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    >
                      H2
                    </Button>
                    <Button
                      variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    >
                      H3
                    </Button>
                    <Button
                      variant={editor.isActive('bulletList') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleBulletList().run()}
                    >
                      • Liste
                    </Button>
                    <Button
                      variant={editor.isActive('orderedList') ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    >
                      1. Liste
                    </Button>
                  </div>
                )}

                {/* Tiptap Editor */}
                <EditorContent editor={editor} />
              </TabsContent>

              {/* Tab 2: HTML Code Editor */}
              <TabsContent value="html" className="mt-0">
                <div className="space-y-4">
                  <div className="bg-muted p-3 rounded-lg text-sm text-muted-foreground">
                    💡 <strong>Tipp:</strong> Hier kannst du HTML-Code von ChatGPT, Claude oder anderen KIs einfügen. 
                    Der Code wird automatisch gespeichert und im Visual Editor angezeigt.
                  </div>
                  
                  <textarea
                    value={editor?.getHTML() || ''}
                    onChange={(e) => editor?.commands.setContent(e.target.value)}
                    className="w-full h-[500px] p-4 border rounded-lg font-mono text-sm bg-card resize-none"
                    placeholder="<h1>Titel</h1>
<p>Dein Content...</p>"
                  />
                  
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    HTML wird automatisch synchronisiert mit dem Visual Editor
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            {/* Footer mit Statistik */}
            <div className="mt-4 text-sm text-muted-foreground">
              Wörter: {editor?.storage.characterCount?.words() || 0} | 
              Zeichen: {editor?.getText().length || 0}
            </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Rechte Spalte: SEO-Panel */}
        <Card className="h-fit sticky top-8">
          <CardHeader>
            <CardTitle>SEO-Einstellungen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Toggle für automatische SEO-Generierung - Modern Design */}
            <div className="relative overflow-hidden rounded-xl border border-border bg-gradient-to-br from-card to-card/50 backdrop-blur-sm shadow-sm">
              <div className="p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                
                {/* Linke Seite: Switch + Label + Live Badge */}
                <div className="flex items-center gap-3 md:gap-4 flex-wrap">
                  
                  {/* Toggle Switch mit optionalem Glow-Ring */}
                  <div className="relative flex-shrink-0">
                    <Switch
                      id="auto-generate-seo"
                      checked={autoGenerateSEO}
                      onCheckedChange={setAutoGenerateSEO}
                      disabled={contentMode !== 'raw'}
                    />
                    {autoGenerateSEO && (
                      <div className="absolute inset-0 -z-10 rounded-full bg-primary/20 blur-md" />
                    )}
                  </div>
                  
                  {/* Label - responsive Text-Größe */}
                  <Label 
                    htmlFor="auto-generate-seo" 
                    className="text-sm md:text-base font-semibold cursor-pointer select-none"
                  >
                    SEO automatisch generieren
                  </Label>
                  
                  {/* Live Badge - kompakt mit Ping-Animation */}
                  {autoGenerateSEO && (
                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                      <div className="relative flex-shrink-0">
                        <div className="h-2 w-2 rounded-full bg-emerald-500" />
                        <div className="absolute inset-0 h-2 w-2 rounded-full bg-emerald-500 animate-ping" />
                      </div>
                      <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                        Live
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Rechte Seite: Action Button - responsive width */}
                <div className="flex items-center w-full sm:w-auto">
                  {seoAlreadyGenerated && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setSeoAlreadyGenerated(false)}
                      className="h-8 md:h-9 px-3 md:px-4 text-xs font-medium hover:bg-primary/10 hover:text-primary transition-colors w-full sm:w-auto"
                    >
                      <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                      Neu generieren
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Subtle Gradient Separator */}
              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
              
              {/* Description Footer mit Icon - responsive */}
              <div className="px-4 md:px-5 py-3 bg-muted/30">
                <p className="text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <svg className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="hidden sm:inline">Beim Speichern werden Meta-Daten automatisch per AI erstellt</span>
                    <span className="sm:hidden">Meta-Daten werden automatisch erstellt</span>
                  </span>
                </p>
              </div>
            </div>

            {contentMode !== 'raw' && (
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg border border-border/50">
                <svg className="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Auto-Generierung nur im Raw HTML Modus verfügbar</span>
              </div>
            )}

            {/* Slug URL */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="slug" className="text-sm font-medium">URL-Slug</Label>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setSlug(generateSlug(metaTitle))}
                  disabled={!metaTitle}
                  className="h-8 px-3 text-xs font-medium hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  <svg className="h-3.5 w-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Aus Meta Title generieren
                </Button>
              </div>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.replace(/\s/g, '-'))}
                placeholder="blog-post-url-slug"
              />
              <p className="text-xs text-muted-foreground">
                URL: /blog/{slug || 'dein-slug-hier'}
              </p>
            </div>

            {/* Meta Title */}
            <div className="space-y-2">
              <Label htmlFor="meta-title">Meta Title</Label>
              <Input
                id="meta-title"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                placeholder="50-60 Zeichen optimal"
                maxLength={100}
              />
              <p className={`text-xs ${getTitleColor(metaTitle.length)}`}>
                {metaTitle.length} Zeichen
                {metaTitle.length >= 50 && metaTitle.length <= 60 && ' ✅ Optimal'}
              </p>
            </div>

            {/* Meta Description */}
            <div className="space-y-2">
              <Label htmlFor="meta-description">Meta Description</Label>
              <Textarea
                id="meta-description"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="150-160 Zeichen optimal"
                rows={4}
                maxLength={250}
              />
              <p className={`text-xs ${getDescriptionColor(metaDescription.length)}`}>
                {metaDescription.length} Zeichen
                {metaDescription.length >= 150 && metaDescription.length <= 160 && ' ✅ Optimal'}
              </p>
            </div>

            {/* Focus Keywords */}
            <div className="space-y-2">
              <Label htmlFor="focus-keyword">Focus Keywords</Label>
              <Input
                id="focus-keyword"
                value={focusKeyword}
                onChange={(e) => setFocusKeyword(e.target.value)}
                placeholder="z.B. Keyword1, Keyword2, Keyword3"
              />
              <p className="text-xs text-muted-foreground">
                3-5 Keywords komma-separiert für SEO-Filterung
              </p>
            </div>

            {/* Kategorie */}
            <div className="space-y-2">
              <Label htmlFor="category">Kategorie</Label>
              <select
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg bg-background text-foreground"
              >
                <option value="">Keine Kategorie</option>
                <option value="cyberversicherung">Cyberversicherung</option>
                <option value="praxistipps">Praxistipps</option>
                <option value="news">News</option>
                <option value="ratgeber">Ratgeber</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Hilft bei der Organisation im Dashboard
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    <Footer />
    </>
  );
}
